import { morphSection } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';

const GIFT_PRODUCT_ID = 10895834710343;
const GIFT_HANDLE = 'cherry-overdrive-zoo-edition';
const SECTION_ID = 'cart-drawer-section';
const DISMISS_KEY = 'zp-gift-dismissed';

// Thresholds in minor units, keyed by cart currency
const THRESHOLDS = { DKK: 50000, EUR: 9900 };

let giftVariantId = null;
let busy = false;
let hadGift = false;

async function getGiftVariantId() {
  if (giftVariantId) return giftVariantId;
  const product = await (await fetch(`/products/${GIFT_HANDLE}.js`)).json();
  const variant = product.variants.find((v) => v.available) ?? product.variants[0];
  giftVariantId = variant.id;
  return giftVariantId;
}

async function cartRequest(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, sections: [SECTION_ID] }),
  });
  const data = await res.json();
  if (data.sections?.[SECTION_ID]) {
    morphSection(SECTION_ID, data.sections[SECTION_ID]);
  }
  return data;
}

async function syncGift() {
  if (busy) return;
  busy = true;

  try {
    const cart = await (await fetch('/cart.js')).json();
    const giftLines = cart.items.filter((i) => i.product_id === GIFT_PRODUCT_ID);
    const giftValue = giftLines.reduce((sum, i) => sum + i.final_line_price, 0);
    const qualifyingTotal = cart.total_price - giftValue;
    const threshold = THRESHOLDS[cart.currency] ?? THRESHOLDS.DKK;
    const qualifies = qualifyingTotal >= threshold;

    if (!qualifies) {
      // Reset dismissal when dropping below the threshold
      sessionStorage.removeItem(DISMISS_KEY);
      if (giftLines.length > 0) {
        await cartRequest('/cart/change.js', { id: giftLines[0].key, quantity: 0 });
      }
      hadGift = false;
      return;
    }

    // Qualifies from here on
    if (giftLines.length === 0) {
      if (hadGift) {
        // Gift vanished while still qualifying → user removed it manually.
        // Respect that for the rest of the session instead of re-adding in a loop.
        sessionStorage.setItem(DISMISS_KEY, '1');
      }
      if (!sessionStorage.getItem(DISMISS_KEY)) {
        const id = await getGiftVariantId();
        await cartRequest('/cart/add.js', {
          items: [{ id, quantity: 1, properties: { _zp_free_gift: 'true' } }],
        });
        hadGift = true;
      }
      return;
    }

    hadGift = true;

    // Enforce quantity of exactly 1
    if (giftLines[0].quantity > 1) {
      await cartRequest('/cart/change.js', { id: giftLines[0].key, quantity: 1 });
    }
  } catch (error) {
    console.warn('[zp-free-gift] sync failed:', error);
  } finally {
    busy = false;
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  // Wait for the theme's own cart update to settle before syncing
  const promise = /** @type {any} */ (event).promise;
  if (promise?.finally) {
    promise.finally(() => syncGift());
  } else {
    setTimeout(syncGift, 150);
  }
});

// Initial check on page load (e.g. returning to a qualifying cart)
syncGift();